import type {EventHashedID, EventVO} from '@Components/events/events.types';
import type {QuestionBelongsTo, TicketingQuestion} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {LoadingStates} from '@Utils/loading.util';

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\AttendanceStatus.php exactly (backend is canonical; it serialises the lowercase ->value).
// Attendance is the attendee's own state only — refund lives on the payment axis (PaymentStatus.REFUNDED), so a
// refunded ticket reads CANCELED here and Refunded on its payment chip.
export enum AttendanceStatus {
  REGISTERED = 'registered',
  CHECKED_IN = 'checked_in',
  CANCELLED = 'cancelled',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\PaymentStatus.php exactly (backend is canonical; it serialises the lowercase ->value).
export enum PaymentStatus {
  UNPAID = 'unpaid',
  CARD = 'card',
  CASH = 'cash',
  REFUNDED = 'refunded',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\OrderAttendanceStatus.php exactly (backend is canonical). This is the
// order-level rollup of the order's tickets' attendance, reusing the per-ticket vocabulary plus the one
// aggregate-only state (PARTIALLY_CHECKED_IN).
export enum OrderAttendanceStatus {
  REGISTERED = 'registered',
  PARTIALLY_CHECKED_IN = 'partially_checked_in',
  CHECKED_IN = 'checked_in',
  CANCELLED = 'cancelled',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\TicketingRefundStatus.php exactly (backend is canonical). ONE dashboard
// refund axis: the in-flight states (REQUESTED = buyer's <24h request awaiting approval; QUEUED = balance-short
// refund awaiting a top-up + retry) win over the settled rollup (PARTIALLY_REFUNDED / REFUNDED). The backend
// mapper combines the fork's pending_refund_state with the settled flags into this single value.
export enum TicketingRefundStatus {
  NONE = 'none',
  REQUESTED = 'requested',
  QUEUED = 'queued',
  PARTIALLY_REFUNDED = 'partially_refunded',
  REFUNDED = 'refunded',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\TicketingRefundSettlement.php exactly (backend is canonical).
// Where a refund already sent to Stripe stands: PENDING = awaiting Stripe's confirmation (its tickets already read
// Refunded); FAILED = Stripe rejected it and nothing else on the order was refunded. Its own axis beside
// TicketingRefundStatus, which doubles as the orders filter vocabulary the fork endpoint translates.
export enum TicketingRefundSettlement {
  PENDING = 'pending',
  FAILED = 'failed',
}

export enum TicketingRefundReason {
  ATTENDEE_REQUESTED = 'ATTENDEE_REQUESTED',
  EVENT_CANCELLED = 'EVENT_CANCELLED',
  DUPLICATE_ORDER = 'DUPLICATE_ORDER',
  PAYMENT_ISSUE = 'PAYMENT_ISSUE',
  OTHER = 'OTHER',
}

// The order's one destructive action: a money-back REFUND (card tickets, refund window open) or a no-money
// CANCEL (everything else — cash, unpaid, free, or card after the 24h cutoff). The attendee-modal footer decides
// which, and the refund sub-panel runs in the matching mode so the button verb and the sub-flow always agree.
export enum TicketingDestructiveAction {
  REFUND = 'refund',
  CANCEL = 'cancel',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\TicketingScanResultStatus.php exactly (backend is canonical). The
// outcome of scanning one ticket QR at the door (B12): a fresh check-in, an already-used ticket, a ticket that
// cannot be admitted (refunded / canceled), or a QR that is not a ticket for this event.
export enum TicketingScanResultStatus {
  CHECKED_IN = 'checked_in',
  ALREADY_CHECKED_IN = 'already_checked_in',
  REFUNDED = 'refunded',
  CANCELLED = 'cancelled',
  NOT_FOUND = 'not_found',
  ON_HOLD = 'on_hold',
  // Neither admitted nor refused: the order has a QUEUED refund and admitting this person spends their share of
  // it, so the card asks first. Nothing has been written server-side yet; answering yes re-runs the scan through
  // confirmScanTicketCheckIn. Never raised for a REQUESTED refund — check-in does not touch that one.
  NEEDS_REFUND_CONFIRMATION = 'needs_refund_confirmation',
}

// Values mirror App\Libraries\Facades\Event\EventTicketing\Enums\TicketingPlanLimitState.php exactly (backend is
// canonical). Where the organiser stands against their plan's ticket/sales allowance. Nothing here stops a sale —
// on a plan that charges more past the allowance, crossing raises the platform fee on the next order and nothing else.
export enum TicketingPlanLimitState {
  NONE = 'none',
  APPROACHING = 'approaching',
  CROSSED = 'crossed',
}

// Mirrors App\...\EventTicketing\TicketingPlanLimitStatus: the plan's ticket/sales allowance, the organiser's
// position against it, and what the platform fee becomes if they pass it. The same object arrives on the dashboard
// payload and on the wizard's fee params, so the banner and the price preview can never quote different numbers.
export interface TicketingPlanLimit {
  state: TicketingPlanLimitState;
  // The plan's ticket allowance; null when the plan declares none (Premium Plus).
  thresholdTickets: number | null;
  // The plan's gross-sales allowance in USD; null when the plan declares none.
  thresholdGross: number | null;
  // Cumulative paid tickets across every event the organiser owns — account-wide, not this event's.
  ticketsSold: number;
  // Cumulative gross processed across every event the organiser owns.
  grossSales: number;
  // The platform rate charged once an allowance is passed, or null when passing one costs nothing (PAYG, already
  // on the top rate). Every surface branches on this field, never on the premium level: null keeps a surface amber
  // and off fee wording, and giving that tier an overage rate later turns it red with no change here.
  overagePercent: number | null;
}

// A zeroed plan limit. Seeds the store and coalesces an absent wire value so no consumer has to null-check before
// reading — NONE renders nothing, which is the correct output when there is nothing to say.
export const getDefaultTicketingPlanLimit = (): TicketingPlanLimit => {
  return {
    state: TicketingPlanLimitState.NONE,
    thresholdTickets: null,
    thresholdGross: null,
    ticketsSold: 0,
    grossSales: 0,
    overagePercent: null,
  };
};

// A value the orders status-filter dropdown can hold: an order status, a refund status, or a payment
// status (the last is cosmetic-only — see TICKETING_STATUS_FILTER_OPTIONS). The backend statuses[] param
// reads the matching order-status and refund-status values; cosmetic-only values are stripped before sending.
export type TicketingStatusFilterValue = OrderAttendanceStatus | TicketingRefundStatus | PaymentStatus;

// Mirrors App\...\EventTicketingDashboard\TicketingSalesByMethod: gross sales per payment method, each
// with the number of tickets behind it. Every count here is TICKETS, so the earnings breakdown's badges
// are all in the same unit as ticketsSold and refundsCount.
export interface TicketingSalesByMethod {
  // card / cash INCLUDE the donations taken by that method (cardDonation / cashDonation).
  card: number;
  // LIVE tickets (cancelled orders excluded).
  cardTicketsSold: number;
  cash: number;
  cashTicketsSold: number;
  unpaidAmount: number;
  unpaidTicketsSold: number;
  // Every ticket on a paid order, cancelled (paid, later refunded) included — the count behind card / cash.
  cardTicketsGross: number;
  cashTicketsGross: number;
  // Every donation on a paid order, returned ones included (unlike TicketingStats.donationTotal), as the donor paid
  // it — the donation line's gross, fee lines too in pass mode — so card / cash minus their slice is ticket money only.
  donation: number;
  cardDonation: number;
  cashDonation: number;
}

// Mirrors App\...\EventTicketingDashboard\TicketingFeeBreakdown: the actual fee charged, plus the rate copy.
export interface TicketingFeeBreakdown {
  amount: number;
  ratePercent: number;
  fixedAmount: number;
}

// Each percentage is null when there is no prior period to compare against.
export interface TicketingStatsTrends {
  ticketsSoldPct: number | null;
  salesPct: number | null;
  earningsPct: number | null;
  refundsPct: number | null;
}

export interface TicketingStats {
  // Live tickets — cancelled and refunded ones are excluded. Shown against `ticketsTotal` as "Tickets sold 12/100",
  // and a cancelled ticket goes back into that capacity, so a gross count would climb past its own denominator.
  // Refunds are the card beside it; they are not netted out of this a second time.
  ticketsSold: number;
  ticketsTotal: number | null;
  totalSalesGross: number;
  // Gross − refunds − PMW fees (net of any fee a fraud / cancellation refund returned). Tax is inside it.
  earnings: number;
  // The tax inside earnings, net of the refunded tickets' share — informational, never a further deduction.
  tax: number;
  totalPayout: number;
  refundsCount: number;
  refundsAmount: number;
  currency: string;
  salesByMethod: TicketingSalesByMethod;
  serviceFee: TicketingFeeBreakdown;
  processingFee: TicketingFeeBreakdown;
  trends: TicketingStatsTrends;
  // "Total donations": the donations still held — face, before fees, less any a settled refund returned (the money is
  // already inside totalSalesGross and earnings).
  donationTotal: number;
  donationCount: number;
  // null when nothing has been donated yet (no average to show).
  donationAverage: number | null;
  // What PMW has already paid out (or has in flight) for the event; the breakdown says so beside Total payout.
  paidOut: number;
}

// Mirrors App\...\EventTicketingDashboard\EventRSVPTicketingStats: the RSVP-event stats row. registrations =
// non-cancelled attendees; cancellations = cancelled attendees. Registrations + page views carry a day-over-day
// (today vs yesterday) trend %, null when yesterday has no baseline; cancellations have no trend.
// The count-based stats, shared by RSVP and free ($0) events. `registrations` is the non-cancelled attendee count
// (for a free event that IS tickets sold, and it matches TicketingStats.ticketsSold's live meaning); `cancellations`
// is what left it. `ticketsTotal` is the ticket capacity — null for RSVP (unlimited), the configured limit for a
// free event (so the row can show "Tickets sold X/Y").
export interface TicketingRsvpStats {
  registrations: number;
  pageViews: number;
  cancellations: number;
  registrationsTrendPct: number | null;
  pageViewsTrendPct: number | null;
  ticketsTotal: number | null;
  // An RSVP / free event with donations on does collect money: "Total donations" and the currency to format it in.
  donationTotal: number;
  donationCount: number;
  donationAverage: number | null;
  currency: string;
  // What the donations came to after fees and returns — the breakdown the "Total donations" card opens.
  donationMoney: TicketingDonationMoney;
}

// Mirrors App\...\EventTicketingDashboard\TicketingDonationMoney. An RSVP / free event's tickets are $0, so every
// figure is donation money: `received` is what donors paid, returned donations included (fee lines too in pass mode);
// the fees and `refunded` come off it down to `earnings`, and `totalPayout` is the card-only part of that;
// `cashCollected` is the cash part (earnings = cash + payout) and `paidOut` what PMW already sent for the event.
export interface TicketingDonationMoney {
  received: number;
  serviceFee: number;
  processingFee: number;
  refunded: number;
  earnings: number;
  totalPayout: number;
  cashCollected: number;
  paidOut: number;
}

// One page of the orders table. Mirrors the backend TicketingOrdersBatch. The page is always complete — the
// backend pushes every filter down to one query, so there is no capped scan that could truncate it.
export interface TicketingOrdersBatch {
  orders: TicketingOrderSummary[];
  currentPageNumber: number;
  lastPage: number;
  total: number;
}

// The single dashboard-load response: the event (for the top bar), its stats, and the first page of orders, in one
// round trip. Exactly one of stats (paid) / rsvpStats (RSVP) / freeStats (free $0) is populated; the rest are null.
export interface TicketingDashboardResponse {
  event: EventVO;
  stats: TicketingStats | null;
  rsvpStats: TicketingRsvpStats | null;
  freeStats: TicketingRsvpStats | null;
  orders: TicketingOrdersBatch;
  // Account-wide, not this event's — the organiser's standing against their plan's allowance.
  planLimit: TicketingPlanLimit;
}

export interface FetchTicketingOrdersArgs {
  eventHashedID: EventHashedID;
  currentPageNumber: number;
  search: string;
  // The organiser's full selection; cosmetic-only rows are stripped before the request.
  statuses: TicketingStatusFilterValue[];
}

export interface FetchTicketingOrderSummariesArgs {
  eventHashedID: EventHashedID;
  // The orders to re-read. Deduplicated by the caller; the backend caps the batch.
  orderIds: number[];
}

export interface CheckInTicketingAttendeeArgs {
  eventHashedID: EventHashedID;
  attendeePublicId: string;
}

// Loading-toast keys for the order-action handlers in event-ticketing-actions.library.ts. (Check-in shows no toast
// — its feedback is the row updating in place + the swipe card's own loader.)
export const RESEND_TICKET_TOAST_KEY = 'ticketing-resend-ticket';
export const MARK_AS_PAID_TOAST_KEY = 'ticketing-mark-as-paid';

export interface MarkTicketOrderAsPaidArgs {
  eventHashedID: EventHashedID;
  orderId: number;
}

export interface CheckInTicketingOrderArgs {
  eventHashedID: EventHashedID;
  orderId: number;
}

export interface CancelTicketingAttendeesArgs {
  eventHashedID: EventHashedID;
  orderId: number;
  // How many of the order's still-active tickets to cancel (no refund) — the first N are cancelled, fungibly.
  ticketQuantity: number;
}

export interface RefundTicketOrderArgs {
  eventHashedID: EventHashedID;
  orderId: number;
  reason: TicketingRefundReason;
  // The organiser's free-text, sent only when `reason` is OTHER; recorded as a PMW audit line, not sent to Hi.Events.
  refundCustomReason?: string;
  // How many tickets to refund. The backend treats "all the active tickets" as a whole-order refund (cancels the
  // order) and a smaller count as a partial refund (cancels exactly those tickets).
  ticketQuantity: number;
}

// What a refund returns: the order's updated table row (merged into the hashmap so the row refreshes in place).
// Mirrors App\...\TicketingRefundResult.
export interface TicketingRefundResult {
  order: TicketingOrderSummary;
  currency: string;
}

// What retrying an order's QUEUED refunds returns (resolve-money P2 f / M5).
export enum TicketingRetryQueuedOutcome {
  RELEASED = 'released', // funded rows fired
  INSUFFICIENT = 'insufficient', // balance still short → nothing fired
  CANCELLED = 'cancelled', // a checked-in covered ticket was voided (A2)
}

// No order-summary row is returned by this endpoint.
export interface TicketingRetryQueuedResult {
  outcome: TicketingRetryQueuedOutcome;
  released?: number;
  voided?: number;
}

// Stable empty reference so the orders selector keeps referential equality when there are no orders to show.
export const EMPTY_TICKETING_ORDERS: TicketingOrderSummary[] = [];

// Fallback currency when stats haven't loaded yet; the dashboard is US-only at launch.
export const DEFAULT_CURRENCY_CODE = 'USD';

// How often the check-in bar re-derives its countdown copy and re-tests its window. A minute is the finest
// resolution the wording shows ("in 40 minutes"), so a shorter tick would re-render for no visible change. It is
// what lets a dashboard left open before doors open roll to "Event has started" on its own.
export const CHECK_IN_BAR_TICK_MILLISECONDS = 60 * 1000;

// Query params the My Stuff check-in banner adds when it deep-links into the dashboard, telling it which door
// surface to open on arrival. Query params rather than redux state because the jump is a full page load. Which one
// the banner uses depends on the device: a phone at the door wants the camera, a desktop wants the searchable list.
export const OPEN_SCANNER_QUERY_PARAM = 'openScanner';
export const OPEN_CHECK_IN_VIEW_QUERY_PARAM = 'openCheckInView';

export interface TicketingOrderSummary {
  orderId: number;
  orderPublicId: string;
  buyerName: string;
  buyerEmail: string;
  // Every ticket ever issued on this order, including cancelled and refunded ones. This is the figure the
  // orders table and the order card show, so a partly-cancelled order still reads as the order the buyer placed.
  // Always equals activeTicketCount + cancelledTicketCount + refundedTicketCount.
  ticketCount: number;
  // Tickets still valid (not cancelled/refunded) — the denominator for the check-in rollup and "x of y" display,
  // and what decides whether the order can still be checked in.
  activeTicketCount: number;
  // Tickets cancelled WITHOUT a refund.
  cancelledTicketCount: number;
  // Tickets refunded.
  refundedTicketCount: number;
  checkedInCount: number;
  orderStatus: OrderAttendanceStatus;
  // null for a free / RSVP order, which owes no money — the row shows no payment chip (it is not "unpaid").
  paymentStatus: PaymentStatus | null;
  // Combined refund situation — in-flight REQUESTED/QUEUED win over the settled PARTIALLY_REFUNDED/REFUNDED; drives
  // the row's refund pill (incl. "Refund requested" / "Refund pending").
  refundStatus: TicketingRefundStatus;
  // A refund sent to Stripe and not yet confirmed, or one Stripe rejected; null otherwise. The row's money pill says so.
  refundSettlement: TicketingRefundSettlement | null;
  // Unix seconds; 0 when Hi.Events sent no created_at. Tie-breaks the status sort (newest first within a group).
  createdAtTimestamp: number;
  // The donation add-on on this order (pre-fee), 0 when none — the Donation column and the Donations tab.
  donationAmount: number;
}

export interface EventTicket {
  attendeePublicId: string;
  orderId: number;
  name: string;
  email: string;
  qrCodeImageUrl: string;
  attendanceStatus: AttendanceStatus;
  // null for a free / RSVP ticket (no money owed) — the ticket card shows no payment pill.
  paymentStatus: PaymentStatus | null;
  isCheckedIn: boolean;
  // The fee-aware amount actually refunded for this ticket (from the refund ledger row that covered it). null when
  // the ticket isn't refunded, or when the rows weren't loaded (only off the single-order fetch) — the footer then
  // shows no figure rather than an averaged guess. 0 on a registration whose order refunded only its donation.
  refundedAmount: number | null;
  // The donation that went back with this ticket's refund, on the ONE ticket that carried it (the last refunded ticket,
  // or the first cancelled registration of an RSVP / free order); null on every other ticket. The footer adds it to
  // refundedAmount, so the tickets' figures sum to what the order's refunds returned.
  refundedDonationAmount: number | null;
}

export interface TicketingQuestionAnswer {
  question: string;
  answer: string;
  // The attendee this answer belongs to, or null for an order-level answer (whoResponds = ORDER). The attendee
  // modal filters the order's one answer list by the active ticket against this (getQuestionAnswersForTicket).
  attendeePublicId: string | null;
}

export interface TicketingOrderDetails {
  orderPublicId: string;
  // Every question answer for the order, each tagged with its attendeePublicId (null = order-level). The modal
  // resolves a ticket's answers from this single list via getQuestionAnswersForTicket — one home, no duplication.
  questionAnswers: TicketingQuestionAnswer[];
  // ORDER = questions answered once for the whole order; PRODUCT = once per attendee. Drives, explicitly, whether
  // a ticket shows all the order's answers or only the ones tagged to its attendee.
  whoResponds: QuestionBelongsTo;
  // The whole order as charged: ticket lines + the donation line, fees included where passed on. The footer's "Amount
  // paid" (or "Amount pending" on an unpaid order, which owes this much).
  totalAmount: number;
  // The ticket lines alone, as charged; 0 on an RSVP / free order. The phone body shows it beside the donation.
  ticketAmount: number;
  // Fee-aware per-ticket refundABLE amount (combined fee never returned) = what one ticket would refund. Drives the
  // refund flow's "Refundable" figure. The already-refunded amount is per-ticket on EventTicket.refundedAmount (from
  // the ledger rows), never an average — so nothing here can misreport a refunded amount.
  perTicketRefundableAmount: number;
  // What refunding every charged ticket not yet refunded sends back, donation included while unreturned — the fork's
  // exact remainder. Quoted whenever a refund empties the order, since per-ticket × count can be a cent off.
  wholeOrderRefundAmount: number;
  // What an in-flight refund currently covers, straight off the pending ledger row, or null when nothing is
  // pending. The Process-refund step shows THIS instead of a recomputed figure: the door adjusts a queued row as
  // tickets are used, so a recomputation can quote a number the release will not pay.
  pendingRefundAmount: number | null;
  // What the order's refunds have returned so far, as Stripe confirmed them (donation included); 0 when nothing went
  // back. The footer says it under "Amount paid" on a live ticket of a partly refunded order.
  totalRefunded: number;
  currency: string;
  // Whether the refund window is still open (decided server-side: refunds are allowed until 24h before the event,
  // cancel-only after). The footer reads this directly instead of re-deriving the rule from the event start.
  isRefundWindowOpen: boolean;
  // Whether the organiser can still CANCEL (decided server-side: only while the event is active, i.e. before it
  // ends). The footer shows the Cancel destructive action only while true — after the event there is nothing to cancel.
  isCancelWindowOpen: boolean;
  // The donation add-on on this order (pre-fee), 0 when none. Never part of the per-ticket figures — the refund that
  // empties the order returns it on top of them (see donationRefundableAmount; wholeOrderRefundAmount includes it).
  donationAmount: number;
  // The donation line as charged: donationAmount in absorb mode, plus its passed-on fees in pass mode. The phone body
  // shows it beside ticketAmount so the two add up to totalAmount; chips and copy keep donationAmount.
  donationChargedAmount: number;
  // What the buyer gets back for the donation when the order is fully refunded: 0 when the order has no donation, or
  // when an earlier refund already returned it. A partial ticket refund leaves it on the order.
  donationRefundableAmount: number;
  // True only on an RSVP / free-ticket order holding a refundable card-paid donation: the organiser's cancel that
  // removes the last registration also refunds it. Paid orders' cancel never moves money (false).
  isDonationRefundedOnFullCancel: boolean;
  // The charged tickets not yet refunded (from the refund ledger): a refund reaching this count empties the order and
  // returns the donation. Not the live ticket count — a money-free cancel leaves its ticket charged and unrefunded.
  refundableTicketQuantity: number;
  paymentMethod: PaymentStatus | null;
  // Combined refund situation (in-flight REQUESTED/QUEUED win over the settled PARTIALLY_REFUNDED/REFUNDED): the
  // modal footer reads this to show the Refund(approve) / Process-refund CTA for a requested/queued order.
  refundStatus: TicketingRefundStatus;
  // A refund sent to Stripe and not yet confirmed, or one Stripe rejected; null otherwise. The ticket pills say so.
  refundSettlement: TicketingRefundSettlement | null;
  // The order's event. The modal is self-contained (it reads no event slice), and it kept needing one more scalar
  // each time it had to know something about the event — RSVP wording, check-in availability — so the payload
  // carries the event itself rather than a growing list of flags.
  event: EventVO;
  tickets: EventTicket[];
}

// The result of scanning one ticket QR at the door (B12), mirroring the PHP TicketingScanResult. The scanner
// shows a result card from it: name/email confirm the person; checkedInAt is the prior check-in time (set only
// for ALREADY_CHECKED_IN); orderId opens the attendee modal via "View details". All but `result` are null for
// NOT_FOUND (the QR is not a ticket for this event).
export interface TicketingScanResult {
  result: TicketingScanResultStatus;
  attendeeName: string | null;
  attendeeEmail: string | null;
  checkedInAt: string | null;
  orderId: number | null;
}

// A ticket QR decoded into its parts. The QR encodes the door-scan URL eventticketing/checkIn/{eventHid}/
// {attendeePublicId} (EventPageURLHelper::getCheckInUrl); the scanner parses it client-side rather than
// navigating to the GET route. null when the scanned text is not a check-in URL at all.
export interface DecodedCheckInQr {
  eventHashedID: EventHashedID;
  attendeePublicId: string;
}

/**
 * One lazy-loaded order list: just the order IDs (the orders themselves live in `ordersHashmap`) plus its
 * paging state. `currentPageNumber` is 0 before the first page loads; more pages remain while it is below
 * `lastPage`. The dashboard keeps two of these — the cached unfiltered list and the fresh filtered list.
 */
export interface TicketingOrdersListLazyData {
  orderIds: number[];
  loadingState: LoadingStates;
  currentPageNumber: number;
  lastPage: number;
  total: number;
}

/**
 * The subset of the backend `App\Libraries\Facades\Event\EventTicketing\Enums\TicketingErrorCode` the dashboard branches on. Wire values
 * must match that enum exactly — the backend emits the code at the top level of its error envelope, which is
 * where core.js `getErrorAjaxResponse` picks it up onto the Error that readLocal throws.
 */
export enum TicketingErrorCode {
  TICKETING_FEATURE_UNAVAILABLE = 'ticketing_feature_unavailable',
}

/** What a rejected dashboard fetch carries, so the reducer can tell a feature gate from a transient failure. */
export interface TicketingDashboardFetchError {
  code: string | null;
}

export interface TicketingDashboardState {
  event: EventVO;
  loadingState: LoadingStates;
  hasFetchEventFailed: boolean;
  // Set only when the backend refused the dashboard because paid ticketing is off for this viewer. Distinct from
  // hasFetchEventFailed (any failure): this one redirects out, a transient failure shows the retry message.
  isTicketingUnavailableForEvent: boolean;
  // Always a populated object (zeroed before load, see getDefaultTicketingStats) — never null — so the stats row
  // always renders its cards with 0s rather than vanishing. Read by paid events; RSVP events read rsvpStats.
  stats: TicketingStats;
  // The RSVP counterpart, also always populated (zeroed before load). RSVP events read this instead of `stats`;
  // all three share statsLoadingState (only one is ever fetched per event).
  rsvpStats: TicketingRsvpStats;
  // The free ($0) counterpart — same count shape as rsvpStats but its own slot so free and RSVP never mix. Free
  // events read this; carries ticketsTotal so the row shows "Tickets sold X/Y". Also always populated (zeroed).
  freeStats: TicketingRsvpStats;
  statsLoadingState: LoadingStates;
  // Every loaded order, by id — shared by both lists below so an order is stored once.
  ordersHashmap: Record<number, TicketingOrderSummary>;
  // The full order list, fetched once on load and kept cached — shown when no search/status filter is active.
  defaultOrders: TicketingOrdersListLazyData;
  // The list for the active search/status filter, re-fetched fresh whenever that filter changes.
  filteredOrders: TicketingOrdersListLazyData;
  // The submitted search term — the one the backend list was fetched with.
  search: string;
  // The term as typed in the search field: filters the loaded orders in place until it is submitted.
  liveSearchTerm: string;
  activeStatusFilters: TicketingStatusFilterValue[];
  // Where the organiser stands against their plan's ticket/sales allowance, across every event they own.
  // Always populated (NONE before load), so the banner and the stat-card warnings never read a null.
  planLimit: TicketingPlanLimit;
}

/** The dashboard is a single live view (not cached per event); revisiting an event re-fetches from scratch. */
export type EventTicketingReducerState = TicketingDashboardState;

export const getDefaultOrdersLazyData = (): TicketingOrdersListLazyData => {
  return {
    orderIds: [],
    loadingState: LoadingStates.NOT_LOADED,
    currentPageNumber: 0,
    lastPage: 1,
    total: 0,
  };
};

/**
 * A zeroed paid-stats object. The store seeds this and coalesces a null wire response to it so the stats row
 * always has real numbers to render (0s) instead of a null that would blank the whole row. ticketsTotal is null
 * (unlimited) and the trends are null (no prior window to compare) until a real fetch fills them in.
 */
export const getDefaultTicketingStats = (): TicketingStats => {
  return {
    ticketsSold: 0,
    ticketsTotal: null,
    totalSalesGross: 0,
    earnings: 0,
    tax: 0,
    totalPayout: 0,
    refundsCount: 0,
    refundsAmount: 0,
    currency: DEFAULT_CURRENCY_CODE,
    salesByMethod: {
      card: 0,
      cardTicketsSold: 0,
      cash: 0,
      cashTicketsSold: 0,
      unpaidAmount: 0,
      unpaidTicketsSold: 0,
      cardTicketsGross: 0,
      cashTicketsGross: 0,
      donation: 0,
      cardDonation: 0,
      cashDonation: 0,
    },
    serviceFee: {amount: 0, ratePercent: 0, fixedAmount: 0},
    processingFee: {amount: 0, ratePercent: 0, fixedAmount: 0},
    trends: {ticketsSoldPct: null, salesPct: null, earningsPct: null, refundsPct: null},
    donationTotal: 0,
    donationCount: 0,
    donationAverage: null,
    paidOut: 0,
  };
};

/** The RSVP counterpart of getDefaultTicketingStats — three zeroed counts so the RSVP cards always render. */
export const getDefaultTicketingRsvpStats = (): TicketingRsvpStats => {
  return {
    registrations: 0,
    pageViews: 0,
    cancellations: 0,
    registrationsTrendPct: null,
    pageViewsTrendPct: null,
    ticketsTotal: null,
    donationTotal: 0,
    donationCount: 0,
    donationAverage: null,
    currency: DEFAULT_CURRENCY_CODE,
    donationMoney: {received: 0, serviceFee: 0, processingFee: 0, refunded: 0, earnings: 0, totalPayout: 0, cashCollected: 0, paidOut: 0},
  };
};

/**
 * Page-level container-query breakpoints for the dashboard content width. The stats row reads these
 * to switch between a four-column layout and a denser two-column grid; later phases (the orders table)
 * can reuse the same object so the whole page reflows from one source of truth.
 *
 * Widths are best-estimate starting points pending design review: above 920px four cards fit comfortably;
 * below that they fold to a 2×2 grid, going compact under 560px (phones).
 */
export enum TicketingDashboardLayoutQuery {
  STATS_FOUR_COLUMNS = 'statsFourColumns',
  STATS_COMPACT = 'statsCompact',
  // Below this the orders table can't fit its columns, so it switches to a horizontally scrollable layout
  // with a pinned actions column. Resilient to narrower widths because the columns scroll.
  TABLE_HORIZONTAL_SCROLL = 'tableHorizontalScroll',
  // On phones the horizontally scrolling table reads poorly, so the orders switch to a vertical card list
  // (TicketingOrdersList). Sits just under the table's horizontal-scroll breakpoint so the list takes over
  // before the columns would get uncomfortably cramped.
  MOBILE_LIST = 'mobileList',
}

export const TICKETING_DASHBOARD_CONTAINER_QUERY: ContainerQueryObject = {
  [TicketingDashboardLayoutQuery.STATS_FOUR_COLUMNS]: {minWidth: 920},
  [TicketingDashboardLayoutQuery.STATS_COMPACT]: {maxWidth: 560},
  [TicketingDashboardLayoutQuery.TABLE_HORIZONTAL_SCROLL]: {maxWidth: 700},
  [TicketingDashboardLayoutQuery.MOBILE_LIST]: {maxWidth: 600},
};

// Mirrors PHP App\Libraries\Facades\Event\EventTicketing\Enums\TicketingAttendeeExportFormat — the file formats the attendee
// download (B10) offers. The value is sent verbatim as the `format` query param of downloadTicketingAttendees.
export enum TicketingAttendeeExportFormat {
  CSV = 'csv',
  PDF = 'pdf',
}

// A checkout question as fetched for the add-attendee form: the event wizard's question shape (text/type/
// required/options — a question is just a question) plus the Hi.Events `id` needed to submit the answer, which
// the wizard/config side deliberately does not store. `QuestionInputType`/`QuestionBelongsTo`/`TicketingQuestion`
// are reused from the wizard so there is one definition of the ticketing-question vocabulary.
export interface TicketingAttendeeQuestion extends TicketingQuestion {
  id: number;
}

// The add-attendee form's config: how many attendees the organiser fills in (whoResponds — one buyer for ORDER,
// one per ticket for PRODUCT) and the questions to ask.
export interface TicketingAddAttendeeFormData {
  whoResponds: QuestionBelongsTo;
  // RSVP relabels the quantity stepper to "registrations"; isFree (a $0 ticket OR RSVP) hides the "Paid in cash"
  // checkbox since no money changes hands. Both are derived server-side from the event's ticketing config.
  isRsvp: boolean;
  isFree: boolean;
  // Server-decided (mirrors EventTicketingFacade::isCheckInAvailableForEvent): false hides the "Add and check in"
  // button, so the form never offers a check-in the add endpoint would ignore.
  isCheckInAvailable: boolean;
  questions: TicketingAttendeeQuestion[];
}

// One answer the organiser fills in (a single value for the text / single-select types, a list for MULTIPLE_CHOICE).
export interface TicketingAttendeeAnswer {
  questionId: number;
  answer: string | string[];
}

// One attendee the organiser fills in — one per ticket for PER_TICKET, a single buyer for PER_ORDER. `name` is a
// single field the backend splits into first/last (the buyer convention).
export interface TicketingAttendeeInput {
  name: string;
  email: string;
  answers: TicketingAttendeeAnswer[];
}

export interface AddTicketingAttendeeArgs {
  eventHashedID: EventHashedID;
  quantity: number;
  paidInCash: boolean;
  // True when the organiser clicked "Check in" (add + check in, in one step) rather than the plain "Add attendee".
  checkInOnAdd: boolean;
  // True when the organiser left the "Send confirmation email" toggle on; the backend emails each added attendee.
  sendConfirmationEmail: boolean;
  attendees: TicketingAttendeeInput[];
}
